import { useState, useEffect, useCallback, useMemo } from 'react';
import { useCityApi } from '../../hooks/useCityApi.js';
import { useCountryApi } from '../../hooks/useCountryApi.js';

export const useAllCitiesViewModel = () => {
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const {
    getCountries,
    cacheOperations: { writeCountries, readCountries }
  } = useCountryApi();

  const {
    getCities,
    deleteCity,
    createCity,
    updateCity: apiUpdateCity
  } = useCityApi();


  const fetchCities = useCallback(async () => {
    if (!loading) setLoading(true);
    try {
      const [citiesResult, countriesResult] = await Promise.all([ getCities(), getCountries()])
      if (citiesResult.success && countriesResult.success) {
        setCities(citiesResult.data);
        writeCountries(countriesResult.data)
      } else {
        setError(citiesResult.error);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [getCities, readCountries, writeCountries, setCities]);


  useEffect(() => {
    fetchCities();
  }, []); 

  const handleDelete = useCallback(async (city) => {
    try {
      const result = await deleteCity(parseInt(city.city_id));
      if (result.success) {
        await fetchCities();
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError(err.message);
    }
  }, [deleteCity, fetchCities]);

  const handleCreateCity = useCallback(async (formData) => {
    try {
      const result = await createCity(formData);
      if (result.success) {
        await fetchCities();
        return { success: true };
      }
    } catch (error) {
      
      throw new Error(error.message);
    }
  }, [createCity, fetchCities]);

  const updateCity = useCallback(async (cityId, formData) => {
    try {
      const result = await apiUpdateCity(cityId, formData);
      if (result.success) {
        await fetchCities();
        return { success: true };
      }
    } catch (error) {
      throw new Error(error.message);
    }
  }, [apiUpdateCity, fetchCities]);

  const getCountryName = useMemo(
    () => (countryId) => {
      const countries = readCountries();
      const country = countries.find(
        (c) => c.country_id.toString() === countryId?.toString()
      );
      return country?.country_name || "Unknown";
    },
    [readCountries]
  );

  return {
    cities,
    loading: loading,
    error,
    refetch: fetchCities,
    handleDelete,
    createCity: handleCreateCity,
    updateCity,
    countries: readCountries(),
    getCountryName
  };
}; 